<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div
      v-if="
        endBeneficiariesSuccessCount.length || endBeneficiariesFailed.length
      "
      class="m-2"
    >
      <b-row>
        <b-col
          v-if="endBeneficiariesSuccessCount.length"
          md="6"
          class="mb-2"
        >
          <b-alert
            show
            variant="primary"
            class="p-2"
          >
            {{ endBeneficiariesSuccessCount }}
          </b-alert>
        </b-col>
      </b-row>

      <b-row>
        <div
          class="mb-3 match-height d-flex align-items-center justify-content-center"
        >
          <b-button variant="danger">
            FAILED LISTS
          </b-button>

          <b-button
            variant="primary"
            class="mx-2"
            @click="handleReupload"
          >
            Re-upload
          </b-button>
        </div>
        <b-col
          v-if="endBeneficiariesFailed.length"
          md="12"
          class="mb-2"
        >
          <b-table
            ref="batchUploadFailedReport"
            :items="endBeneficiariesFailed"
            responsive
            :fields="failedHeaders"
            primary-key="id"
            show-empty
            empty-text="No records available"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            class="position-relative"
          >
            <template #cell(SN)="data">
              {{ getOverallIndex(data.index) }}
            </template>
          </b-table>
        </b-col>
      </b-row>
    </div>

    <div
      v-if="!endBeneficiariesFailed.length"
      class="m-2"
    >
      <b-row class="d-flex align-items-center">
        <b-col md="4">
          <validation-observer ref="batchIndividualCustomerRules">
            <b-form>
              <b-col
                md="12"
                class="mb-2"
              >
                <div class="mb-1">
                  Select file (.xlsx format only)
                </div>
                <b-form-file
                  id="file-name"
                  v-model="hasFileData"
                  label="Select File (.xlsx format only)"
                  :state="Boolean(hasFileData)"
                  placeholder="Choose a file or drop it here..."
                  drop-placeholder="Drop file here..."
                  error-name="file"
                  rules="required"
                  accept=".xlsx"
                  @change="onChangeXLSX($event)"
                />
              </b-col>
            </b-form>
          </validation-observer>
        </b-col>

        <b-col md="8">
          <b-row class="d-flex align-items-center">
            <b-col
              md="3"
              class="mt-2"
            >
              <SwitchButton
                v-model="enablePreview"
                label="Enable Preview?"
                name="enable-preview"
              />
            </b-col>
            <b-col md="3">
              <Button
                v-if="parseExcelFile.length > 0"
                label="Create End Beneficiaries"
                size="md"
                :loading="isLoading"
                :disabled="isDisabled"
                @click="handleSubmit"
              />
            </b-col>
            <b-col md="6">
              <Button
                class="ml-lg-2"
                align="end"
                label="Download Batch Template"
                size="md"
                icon="DownloadIcon"
                show-icon
                :loading="isLoading2"
                :disabled="isDisabled2"
                @click="handleDownloadSubmit"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
    <b-table
      v-if="errorMessages.length"
      ref="batchUploadSuccessCountReport"
      :items="errorMessages"
      responsive
      :fields="errorHeaders"
      primary-key="id"
      show-empty
      empty-text="No records available"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    />

    <p
      v-if="totalSheetRows && !enablePreview"
      class="p-2"
      style="font-size: 18px"
    >
      There are total of ({{ totalSheetRows }}) Records fetched
    </p>
    <b-table
      v-if="enablePreview && !endBeneficiariesFailed.length"
      ref="batchUploadValidationRef"
      :items="parseExcelFile"
      responsive
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :fields="tableHeaders"
      primary-key="id"
      show-empty
      empty-text="No records available"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
    </b-table>

    <TablePagination
      v-if="enablePreview && !endBeneficiariesFailed.length"
      v-model="currentPage"
      name="table-pagination"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BForm,
  BTable,
  BFormFile,
  BButton,
  BAlert,
} from 'bootstrap-vue'
import { required } from '@validations'
import { ValidationObserver } from 'vee-validate'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import XLSX from 'xlsx'
import Button from '../../components/button/Button.vue'
import SwitchButton from '../../components/switch/Switch.vue'
import { batchAccountFormat, tableAllHeaders } from '../../../helpers/data'
import TablePagination from '@/views/components/table/TablePagination.vue'
import {
  toKobo,
  containsFormattedNumber,
  removeCommas,
} from '@/helpers/formatAmount'

export default {
  components: {
    BCard,
    BTable,
    BRow,
    BCol,
    BForm,
    BAlert,
    BFormFile,
    ValidationObserver,
    Button,
    BButton,
    SwitchButton,
    TablePagination,
  },
  filters: {
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      enablePreview: false,
      totalSheetRows: '',
      fileData: [],
      hasFileData: [],
      parseExcelFile: [],
      validatedExcelFileData: [],
      sortOrders: {},
      accountFormat: batchAccountFormat,
      successHeaders: tableAllHeaders,
      failedHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'beneficiaryName', sortable: true },
        { key: 'validationMessage', sortable: true },
        // { key: "kycValidationStatus", sortable: true },
      ],
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'beneficiaryName', sortable: true },
        { key: 'bvn', sortable: true },
        { key: 'nin', sortable: true },
        { key: 'gender', sortable: true },
        { key: 'dateOfBirth', sortable: true },
        { key: 'employmentStatus', sortable: true },
        { key: 'loanAmountAccessed', sortable: true },
        { key: 'sector', sortable: true },
      ],
      required,
      isDisabled: false,
      isLoading: false,
      isDisabled2: false,
      isLoading2: false,
      showFormError: false,
      isCreateBatchHidden: true,
      endBeneficiariesSuccessCount: [],
      endBeneficiariesFailed: [],
      sortDesc: false,
      sortBy: 'lastName',
      errorHeaders: [
        { key: 'referenceNumber', sortable: true, label: 'Reference Number' },
        { key: 'message', sortable: true, label: 'Error Messages' },
      ],
      errorMessages: [],
    }
  },
  methods: {
    ...mapActions({
      createEndBeneficiaries: 'llms/createEndBeneficiariesAction',
      validateEndBeneficiaries: 'llms/validateEndBeneficiariesAction',
    }),
    async handleSubmit() {
      this.$refs.batchIndividualCustomerRules.validate().then(success => {
        if (success) {
          this.confirm(this.validateEndBeneficiariesAction, {
            text: "Would you like to validate the End Beneficiaries' data before proceeding with their creation?",
            confirmButtonText: 'Yes, validate the data',
            cancelButtonText: 'Skip validation',
            cancelCallback: this.batchEndBeneficiariesAction,
          })
        }
      })
    },
    // Reset the form and clear results for reupload
    handleReupload() {
      this.endBeneficiariesSuccessCount = []
      this.endBeneficiariesFailed = []
      this.hasFileData = []
      this.parseExcelFile = []
      this.errorMessages = []
      this.totalSheetRows = ''
      this.enablePreview = false
      this.showFormError = false
    },
    // Common function to format data for both create and validate actions
    prepareFormData() {
      return this.parseExcelFile.map(item => ({
        ...item,
        gender: item.gender ? Number(item.gender) : null,
        employmentStatus: item.employmentStatus
          ? Number(item.employmentStatus)
          : null,
        sector: item.sector ? Number(item.sector) : null,
        dateOfBirth: item.dateOfBirth ? new Date(item.dateOfBirth) : null,
        loanAmountAccessed: containsFormattedNumber(item.loanAmountAccessed)
          ? toKobo(removeCommas(item.loanAmountAccessed))
          : toKobo(item.loanAmountAccessed),
      }))
    },
    // Common function to handle API responses
    handleApiResponse(response, successMessage = null) {
      const { status, data } = response
      if (status === 200) {
        this.hasFileData = []
        this.parseExcelFile = []

        if (data.successCount) {
          this.endBeneficiariesSuccessCount = `Successfully ${successMessage} (${data.successCount}) End Beneficiaries.`
        }

        this.endBeneficiariesFailed = data.failed.map(item => ({
          ...item,
          beneficiaryName: item.referenceNumber,
          validationMessage: item.message,
        }))

        this.totalSheetRows = ''
      }
    },
    // Common function to handle API errors
    handleApiError(error, actionType) {
      error.response.data.errors.forEach(responseError => {
        this.alertError({
          text: `Your request to ${actionType} End Beneficiaries failed. Reason: ${responseError.errorMessage}`,
        })
      })
      this.showFormError = true
    },
    async performAction(actionType, apiCall, successMessage = null) {
      try {
        this.endBeneficiariesSuccessCount = ''
        this.showFormError = false
        this.isDisabled = true
        this.isLoading = true

        const formData = this.prepareFormData()
        const response = await apiCall({ endBeneficiaries: formData })

        this.handleApiResponse(response, successMessage)
      } catch (error) {
        this.handleApiError(error, actionType)
      } finally {
        this.isDisabled = false
        this.isLoading = false
      }
    },
    async batchEndBeneficiariesAction() {
      await this.performAction(
        'create',
        this.createEndBeneficiaries,
        'processed',
      )
    },
    async validateEndBeneficiariesAction() {
      await this.performAction(
        'validate',
        this.validateEndBeneficiaries,
        'validated',
      )
    },
    onChangeXLSX(event) {
      this.errorMessages = []
      this.parseExcelFile = []
      this.isCreateBatchHidden = true
      this.fileData = event.target.files ? event.target.files[0] : null
      if (this.fileData) {
        const vm = this
        const reader = new FileReader()
        reader.onload = e => {
          const data = e.target.result
          const workbook = XLSX.read(data, {
            type: 'binary',
            cellDates: true,
            dateNF: 'mm/dd/yyyy;@',
          })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const rowObject = XLSX.utils.sheet_to_row_object_array(worksheet, {
            raw: false,
          })
          vm.totalSheetRows = rowObject.length
          vm.parseExcelFile = JSON.parse(JSON.stringify(rowObject)).map(
            items => ({
              beneficiaryName: items['Beneficiary Name'],
              bvn: items.BVN,
              nin: items.NIN,
              gender: items.Gender,
              dateOfBirth: items['Date of Birth'],
              employmentStatus: items['Employment Status'],
              loanAmountAccessed: items['Loan Amount Accessed'],
              sector: items.Sector,
            }),
          )
          this.totalCount = vm.parseExcelFile.length
        }
        reader.readAsBinaryString(this.fileData)
      }
    },
    async handleDownloadSubmit() {
      this.confirm(this.downloadAction, {
        text: 'Would you like to download the End Beneficiaries template?',
      })
    },
    async downloadAction() {
      try {
        this.isLoading2 = true
        this.isDisabled2 = true
        const INDIVIDUAL_CUSTOMER_BIODATA_UPLOAD_TEMPLATE = 19
        const response = await this.downloadAccountFormat(
          INDIVIDUAL_CUSTOMER_BIODATA_UPLOAD_TEMPLATE,
        )
        if (response.status === 200) {
          const downloadAnchor = document.createElement('a')
          downloadAnchor.setAttribute('href', response.data.data)
          const filename = 'Udara360-Batch-Account-Creation.xlsx'
          downloadAnchor.setAttribute('download', filename)
          document.body.appendChild(downloadAnchor)
          downloadAnchor.click()
          document.body.removeChild(downloadAnchor)
          this.alertSuccess({
            text: 'Your request to download End Beneficiaries Format was successful',
          })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.alertError({
            text: `Your request to download End Beneficiaries Format failed. Reason: ${responseError.errorMessage}`,
          })
        })
      } finally {
        this.isLoading2 = false
        this.isDisabled2 = false
      }
    },
  },
}
</script>
